import { format } from 'date-fns';
import { X, Download } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { LegalDocumentRenderer } from '@/components/legal/LegalDocumentRenderer';
import { type LegalDocument } from '@/hooks/useLegalDocuments';

interface LegalDocumentPreviewModalProps {
  document: LegalDocument | null;
  onClose: () => void;
}

export function LegalDocumentPreviewModal({
  document,
  onClose,
}: LegalDocumentPreviewModalProps) {
  if (!document) return null;

  const handleDownload = () => {
    const blob = new Blob([document.content], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = window.document.createElement('a');
    a.href = url;
    a.download = `${document.document_type}-v${document.version}.md`;
    window.document.body.appendChild(a);
    a.click();
    window.document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const getStatusBadge = () => {
    if (document.is_active) {
      return <Badge className="bg-green-500/10 text-green-600 border-green-500/20">Active</Badge>;
    }
    if (document.activated_at) {
      return <Badge variant="secondary">Archived</Badge>;
    }
    return <Badge variant="outline" className="text-yellow-600 border-yellow-500/50">Draft</Badge>;
  };

  return (
    <Dialog open={!!document} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl h-[90vh] flex flex-col p-0">
        <DialogHeader className="px-6 py-4 border-b flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <DialogTitle className="text-xl">{document.title}</DialogTitle>
              <div className="flex items-center gap-3 text-sm text-muted-foreground">
                <span>Version {document.version}</span>
                {document.effective_date && (
                  <>
                    <span>•</span>
                    <span>Effective: {format(new Date(document.effective_date), 'MMMM d, yyyy')}</span>
                  </>
                )}
                <span>•</span>
                {getStatusBadge()}
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={handleDownload}>
              <Download className="h-4 w-4 mr-2" />
              Download
            </Button>
          </div>
        </DialogHeader>
        <ScrollArea className="flex-1 px-6 py-4">
          <LegalDocumentRenderer content={document.content} />
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
